import Vue from 'vue';
import Router from 'vue-router';

import moment from 'moment';
import EmptyRouterView from '@/views/shared/EmptyRouterView.vue';
import ErrorView from '@/views/shared/ErrorPageV2.vue';

import RouterMiddleware from '@/router/middleware';
import log from '@/services/log';
import helperFunctions from '@/utils/helperFunctions';
import { AVAnalyticsEventTriggers } from '@/utils/enums';
import embedRoute from './embed/index';
import desktopRoute from './desktop/index';
import mobileRoute from './mobile/index';
import store from '../store/index';
import referralType from '@/utils/enums/referralType';

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: '/:country(us)?',
      component: {
        render(c) {
          return c('router-view');
        },
      },
      children: [
        {
          path: '',
          redirect: 'mobile', // We can set a default url to /mobile
        },
        {
          path: 'mobile',
          component: EmptyRouterView,
          children: mobileRoute,
        },
        {
          path: 'desktop',
          component: EmptyRouterView,
          children: desktopRoute,
        },
        {
          path: 'embed',
          component: EmptyRouterView,
          children: embedRoute,
        },
        {
          path: 'error',
          component: ErrorView,
          name: 'general-error',
          meta: {
            headerIcon: false,
            navInfo: false,
            hasNext: false,
            errorPayload: null,
          },
        },
        {
          path: 'session-expired',
          component: ErrorView,
          name: 'session-expired',
          meta: {
            headerIcon: false,
            navInfo: false,
            hasNext: false,
            errorPayload: {
              errorType: 'expired',
            },
          },
        },
      ],
    },
  ],
  scrollBehavior() {
    // Make a scrollBehavior working
    setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    return { x: 0, y: 0 };
  },
});
const excludedPathsUpdateMetaData = ['test-drive/request-submitted', 'finance-application/thank-you', 'acquire/report'];
const excludedPathsForLeadUpdate = ['finance/employment-details-form', 'finance/employment-contact-details-form'];
// This router hook will keep the query string
const hasQueryParams = route => !!Object.keys(route.query).length;
router.beforeEach((to, from, next) => {
  if (helperFunctions.preventRouteChange(to, from)) {
    next(false);
    return;
  }

  if (!to.matched.length) {
    log('Routing error', `Failed route: ${to.path}`);
  }

  if (!hasQueryParams(to) && hasQueryParams(from)) {
    // copy query parameters to 'to' route
    const toWithQuery = Object.assign({}, to, {
      query: { ...from.query, ...to.query },
      params: { ...from.params, ...to.params },
    });
    next(toWithQuery);
    return;
  }

  if (from.meta.navInfo && to.meta.navInfo) {
    if (from.meta.navInfo.to > to.meta.navInfo.from) {
      // eslint-disable-next-line no-param-reassign
      to.meta.navInfo.from = from.meta.navInfo.to;
    } else {
      // eslint-disable-next-line no-param-reassign
      to.meta.navInfo.from = to.meta.navInfo.to - 1;
    }
  }

  if (to.query.referralType && to.query.referralType === referralType.THIRD_PARTY_MOBILE_APPLICATION) {
    const expirationHours = 48;
    const nowInUtc = moment().utc();
    if (to.query.timeStamp) {
      const timeStampInUtcTime = moment.utc(to.query.timeStamp);
      const differenceInHours = nowInUtc.diff(timeStampInUtcTime, 'hours');
      if (differenceInHours >= expirationHours) {
        next({ name: 'session-expired' });
        return;
      }
    } else {
      // eslint-disable-next-line no-param-reassign
      to.query.timeStamp = nowInUtc.toISOString();
    }
  }

  if (from.meta.skipImpression && to.meta.headerIcon === 'back') {
    // Hide back button as there is no previous route to go to in this scenario
    // eslint-disable-next-line no-param-reassign
    to.meta.headerIcon = 'none';
  }

  /**
   * Publish the Route Change so the SDK can relay to GA if it's integrated
   */
  window.parent.postMessage({ path: to.path }, '*');

  // We also store all the route updates in our store
  // Replace route list when it's just country param update
  if (from.name && !from.params.country && to.params.country === 'us') {
    store.commit('UPDATE_ROUTE_LIST', { to: to.path, from: from.path });
  } else {
    store.commit('ADD_ROUTE', to.path);
  }
  // hide Equifax iFrame component on entry/exit of route
  if ((from.name && from.name.includes('form-review')) || (to.name && to.name.includes('form-review'))) {
    store.state.usCredit.equifax.showEquifaxIframe = false;
  }

  // ecomV2 complete lead
  // set loading to true if we are going to try to complete the lead
  // Loading set to false in afterEach
  if (
    to.path.includes('e-commerce-v2') &&
    to.meta &&
    to.meta.completeLeadOnEnter &&
    store.getters['lead/leadIsActive']
  ) {
    store.commit('SET_LOADING', true);
  }

  next();
});

// eslint-disable-next-line no-unused-vars

router.afterEach(async to => {
  // EcomV2 and B&P update and complete leads
  // Called after the route changes so that we
  // capture the new route in the route list
  if (
    (to.path.includes('e-commerce-v2') || to.path.includes('build-and-price')) &&
    to.meta &&
    to.meta.updateLeadOnEnter !== false &&
    store.getters['lead/leadIsActive']
  ) {
    try {
      try {
        await store.dispatch('lead/updateMetadata');
      } catch (error) {
        router.push({ path: '/error' });
      }
      if (!excludedPathsForLeadUpdate.some(path => to.path.includes(path))) {
        await store.dispatch('lead/updateLead');
      }
      if (to.meta.completeLeadOnEnter && to.path.includes('e-commerce-v2')) {
        try {
          await store.dispatch('lead/completeLead');
        } catch (error) {
          log('Error in complete lead on enter', error);
        }
      }
    } catch (error) {
      log('Error in update lead on enter', error);
      router.push('error');
    } finally {
      if (to.meta.completeLeadOnEnter && to.path.includes('e-commerce-v2')) {
        // Loading set to true in beforeEnter
        store.commit('SET_LOADING', false);
      }
    }
  } else if (store.getters['lead/leadIsActive'] && !excludedPathsUpdateMetaData.some(path => to.path.includes(path))) {
    try {
      await store.dispatch('lead/updateMetadata');
    } catch (error) {
      router.push({ path: '/error' });
    }
  }
  if (!('skipImpression' in to.meta) || to.meta.skipImpression !== true) {
    // Call the impression endpoint on every route change
    RouterMiddleware(to, store);
  }

  // Trigger custom analytics events based on route change
  // don't fire if the app is not initialized yet (first page load)
  // because we might not have all the data we need yet
  if (store.state.isInitialized) {
    helperFunctions.sendAVAnalyticsEvent(to.path, AVAnalyticsEventTriggers.PAGE_VIEW, store);
  }
});

export default router;
